import { ShieldExclamationIcon } from '@heroicons/react/24/outline';
import React from 'react';

import { CopyToClipboard } from '@/components/CopyToClipboard';
import {
  extractField,
  parseDataFindingExamples,
} from '@/sections/poe/processors/wiz/parsers';
import type {
  WizParsedData,
  WizRenderContext,
} from '@/sections/poe/processors/wiz/types';
import { safeDecodeURIComponent } from '@/utils/displayIdentifier.util';

export function renderMultiSection(
  parsed: WizParsedData,
  ctx: WizRenderContext
): void {
  const { wizData, wizId, isMultiSectionFormat } = parsed;
  const { sections, tempTabs, mainTabKeys } = ctx;

  if (!isMultiSectionFormat || !mainTabKeys.includes('DETECTION')) return;

  // Preserve original if/else-if exclusion: when Data Finding Examples
  // was an array, the PII renderer handles it and multi-section is skipped.
  if (
    parsed.dataFindingExamplesArray &&
    parsed.dataFindingExamplesArray.length > 0
  )
    return;

  // prettier-ignore
  sections['wiz_multi_section'] = (
    <div className="space-y-6">
      <div className="border-error border-l-4 p-4">
        <div className="flex items-center">
          <ShieldExclamationIcon className="text-error mr-2 size-5" />
          <h4 className="text-chariot-text-primary font-semibold">Multi-Resource Security Finding</h4>
        </div>
      </div>
      {wizId && (
        <div className="border-chariot-stroke rounded-lg border p-4">
          <div className="flex items-center justify-between">
            <h5 className="text-chariot-text-primary font-medium">Wiz Finding ID</h5>
            <CopyToClipboard iconClassName="size-3 text-chariot-text-secondary" alwaysShowCopyIcon textToCopy={wizId}>
              <code className="bg-chariot-background-secondary rounded px-2 py-1 text-xs">{wizId}</code>
            </CopyToClipboard>
          </div>
        </div>
      )}
      {wizData.bucket && (
        <div className="border-chariot-stroke rounded-lg border p-4">
          <h5 className="text-chariot-text-primary mb-3 flex items-center font-medium">
            <div className="bg-chariot-stroke-blue mr-2 size-4 rounded"></div>
            S3 Bucket Information
          </h5>
          <div className="space-y-2 text-sm">
            {(() => {
              const bucketName = extractField(wizData.bucket, 'Name');
              return (
                bucketName && (
                  <div className="flex items-start gap-3">
                    <span className="text-chariot-text-secondary min-w-16 font-medium">Bucket:</span>
                    <CopyToClipboard iconClassName="size-3 text-chariot-text-secondary" alwaysShowCopyIcon textToCopy={bucketName}>
                      <code className="bg-chariot-background-secondary rounded px-2 py-1 font-mono">{bucketName}</code>
                    </CopyToClipboard>
                  </div>
                )
              );
            })()}
            {extractField(wizData.bucket, 'Type') && (
              <div className="flex items-start gap-3">
                <span className="text-chariot-text-secondary min-w-16 font-medium">Type:</span>
                <span>{extractField(wizData.bucket, 'Type')}</span>
              </div>
            )}
          </div>
        </div>
      )}
      {wizData['data finding'] && (
        <div className="border-chariot-stroke rounded-lg border p-4">
          <h5 className="text-chariot-text-primary mb-3 flex items-center font-medium">
            <div className="bg-chariot-stroke-critical mr-2 size-4 rounded"></div>
            Data Finding - PII Detection
          </h5>
          <div className="space-y-3">
            <div className="space-y-2 text-sm">
              {extractField(wizData['data finding'], 'Name') && (
                <div className="flex items-start gap-3">
                  <span className="text-chariot-text-secondary min-w-20 font-medium">Finding:</span>
                  <span className="text-error font-medium">{extractField(wizData['data finding'], 'Name')}</span>
                </div>
              )}
              {extractField(wizData['data finding'], 'Type') && (
                <div className="flex items-start gap-3">
                  <span className="text-chariot-text-secondary min-w-20 font-medium">Type:</span>
                  <span>{extractField(wizData['data finding'], 'Type')}</span>
                </div>
              )}
            </div>
            {(() => {
              const examples = parseDataFindingExamples(wizData['data finding'] || '', wizData);
              if (examples.length > 0) {
                return (
                  <div className="border-chariot-stroke border-t pt-3">
                    <div className="mb-2 flex items-center justify-between">
                      <span className="text-chariot-text-secondary font-medium">Data Examples:</span>
                      <span className="text-chariot-text-tertiary text-xs">{examples.length} matches found</span>
                    </div>
                    <div className="bg-chariot-background-secondary rounded-lg p-3">
                      <div className="text-chariot-text-secondary mb-2 text-xs">Sample detected values:</div>
                      <div className="space-y-1">
                        {examples.slice(0, 5).map(
                          // eslint-disable-next-line @typescript-eslint/no-explicit-any
                          (example: any, idx: number) => (
                            <div key={idx} className="text-chariot-text-primary font-mono text-sm">
                              {example.match || example}
                            </div>
                          )
                        )}
                        {examples.length > 5 && (
                          <div className="text-chariot-text-secondary text-xs italic">
                            ... and {examples.length - 5} more matches
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              }
              return null;
            })()}
          </div>
        </div>
      )}
      {wizData.endpoint && (
        <div className="border-chariot-stroke rounded-lg border p-4">
          <h5 className="text-chariot-text-primary mb-3 flex items-center font-medium">
            <div className="bg-chariot-stroke-info mr-2 size-4 rounded"></div>
            API Endpoint
          </h5>
          <div className="space-y-2 text-sm">
            {(() => {
              const endpointName = extractField(wizData.endpoint, 'Name');
              return (
                endpointName && (
                  <div className="flex items-start gap-3">
                    <span className="text-chariot-text-secondary min-w-16 font-medium">URL:</span>
                    <CopyToClipboard iconClassName="size-3 text-chariot-text-secondary" alwaysShowCopyIcon textToCopy={endpointName}>
                      <code className="bg-chariot-background-secondary rounded px-2 py-1 font-mono break-all">{endpointName}</code>
                    </CopyToClipboard>
                  </div>
                )
              );
            })()}
            {extractField(wizData.endpoint, 'Type') && (
              <div className="flex items-start gap-3">
                <span className="text-chariot-text-secondary min-w-16 font-medium">Type:</span>
                <span>{extractField(wizData.endpoint, 'Type')}</span>
              </div>
            )}
          </div>
        </div>
      )}
      {wizData['iam binding'] && (
        <div className="border-chariot-stroke rounded-lg border p-4">
          <h5 className="text-chariot-text-primary mb-3 flex items-center font-medium">
            <div className="bg-chariot-stroke-medium mr-2 size-4 rounded"></div>
            IAM Permissions
          </h5>
          <div className="space-y-2 text-sm">
            {extractField(wizData['iam binding'], 'Name') && (
              <div className="flex items-start gap-3">
                <span className="text-chariot-text-secondary min-w-20 font-medium">Permissions:</span>
                <span className="text-warning font-medium">{extractField(wizData['iam binding'], 'Name')}</span>
              </div>
            )}
            {extractField(wizData['iam binding'], 'Type') && (
              <div className="flex items-start gap-3">
                <span className="text-chariot-text-secondary min-w-20 font-medium">Type:</span>
                <span>{extractField(wizData['iam binding'], 'Type')}</span>
              </div>
            )}
          </div>
        </div>
      )}
      {wizData['service account'] && (
        <div className="border-chariot-stroke rounded-lg border p-4">
          <h5 className="text-chariot-text-primary mb-3 flex items-center font-medium">
            <div className="bg-chariot-text-purple mr-2 size-4 rounded"></div>
            Service Account
          </h5>
          <div className="space-y-2 text-sm">
            {(() => {
              const accountName = extractField(wizData['service account'], 'Name');
              return (
                accountName && (
                  <div className="flex items-start gap-3">
                    <span className="text-chariot-text-secondary min-w-16 font-medium">Account:</span>
                    <CopyToClipboard iconClassName="size-3 text-chariot-text-secondary" alwaysShowCopyIcon textToCopy={accountName}>
                      <code className="bg-chariot-background-secondary rounded px-2 py-1 font-mono">{accountName}</code>
                    </CopyToClipboard>
                  </div>
                )
              );
            })()}
            {extractField(wizData['service account'], 'Type') && (
              <div className="flex items-start gap-3">
                <span className="text-chariot-text-secondary min-w-16 font-medium">Type:</span>
                <span>{extractField(wizData['service account'], 'Type')}</span>
              </div>
            )}
          </div>
        </div>
      )}
      {(() => {
        let piiDataArray = null;
        if (
          Array.isArray(wizData.dataFindingExamples) &&
          wizData.dataFindingExamples.length > 0
        ) {
          piiDataArray = wizData.dataFindingExamples;
        } else {
          for (const value of Object.values(wizData)) {
            if (
              Array.isArray(value) &&
              value.length > 0 &&
              value[0] &&
              typeof value[0] === 'object' &&
              (value[0].value || value[0].valueForAnalytics || value[0].path)
            ) {
              piiDataArray = value;
              break;
            }
          }
        }
        return (
          piiDataArray &&
          piiDataArray.length > 0 && (
            <div className="border-chariot-stroke rounded-lg border p-4">
              <h5 className="text-chariot-text-primary mb-3 flex items-center font-medium">
                <div className="bg-chariot-stroke-critical mr-2 size-4 rounded"></div>
                PII Data Detection
              </h5>
              <div className="space-y-3">
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-chariot-text-secondary font-medium">Detected Examples:</span>
                  <span className="text-chariot-text-tertiary text-xs">{piiDataArray.length} matches found</span>
                </div>
                <div className="bg-chariot-background-secondary rounded-lg p-3">
                  <div className="text-chariot-text-secondary mb-2 text-xs">Sample detected values:</div>
                  <div className="space-y-1">
                    {piiDataArray.slice(0, 10).map(
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      (example: any, idx: number) => (
                        <div key={idx} className="space-y-1">
                          <div className="flex items-center justify-between text-sm">
                            <code className="text-chariot-text-primary font-mono">
                              {example.value ||
                                example.valueForAnalytics ||
                                example.match ||
                                example}
                            </code>
                            {example.pathTotalMatches && (
                              <span className="text-chariot-text-tertiary text-xs">
                                {example.pathTotalMatches.Value ||
                                  example.pathTotalMatches}{' '}
                                matches
                              </span>
                            )}
                          </div>
                          {example.path && (
                            <div className="text-chariot-text-tertiary text-xs break-all">
                              Path: {safeDecodeURIComponent(example.path)}
                            </div>
                          )}
                        </div>
                      )
                    )}
                    {piiDataArray.length > 10 && (
                      <div className="border-chariot-stroke text-chariot-text-secondary border-t pt-2 text-xs italic">
                        ... and {piiDataArray.length - 10} more matches
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )
        );
      })()}
    </div>
  );
  tempTabs['DETECTION'].push({
    label: 'Multi-Resource Finding',
    value: 'wiz_multi_section',
  });
}
